import React, { useState } from 'react'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { IEpiProps, IEventRegistrationRefreshModel, IUserSelections, StepConstants, IAttendee } from '../models/event-reg-refresh-models'
import { buildDateRangeString, getDateTimeEventLocation } from '../helpers/helpers'
import { Alert } from './alert'
import { LoadingSpinner } from './loading-spinner'
import { ProgramSummarySelections } from './program-summary-selections'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

interface IProps {
  userSelections: IUserSelections
  onContinue: () => void
  showValidationMessage: boolean
  validationMessage: string
  hideContinueButton?: boolean
  showShareSelectionsModalButton?: boolean //Only for date/time steps and review step.
  onShowShareSelectionsModal?: () => void; //Only for date/time steps and review step.
  // Optional props for attendee-specific features
  showMaxAttendeesMessage?: boolean
  maxAttendeesMessage?: string
  isSubmitting?: boolean
  showWarningMessage?: boolean
  warningMessage?: string
}

export const ProgramSummary = (props: IProps) => {
  const { currentStep, epiProps, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!
  const [isOpen, setIsOpen] = useState(false)

  // Check what content should be shown
  let showSelectedProgram = props.userSelections
    && props.userSelections.userProgramSelection
    && props.userSelections.userProgramSelection.programName
    && props.userSelections.userProgramSelection.programName.length > 0;

  let showSelectedAddOnProgram = props.userSelections
    && (props.userSelections.userAddOnProgramSelections || []).length > 0;

  let showSelectedProgramDateTimeSelection = props.userSelections
    && props.userSelections.userProgramDateTimeSelection
    && getDateTimeEventLocation(props.userSelections.userProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)
    && getDateTimeEventLocation(props.userSelections.userProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard).length > 0;

  let showSelectedAddOnProgramDateTimeSelection = props.userSelections
    && props.userSelections.userAddOnProgramDateTimeSelection
    && getDateTimeEventLocation(props.userSelections.userAddOnProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)
    && getDateTimeEventLocation(props.userSelections.userAddOnProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard).length > 0;

  // Get selected program details for pricing (when attendees are present)
  const selectedProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
    p => p.programCode === props.userSelections.userProgramSelection?.programCode &&
      p.isRenewal === props.userSelections.userProgramSelection.isRenewal)

  const attendeeCount = props.userSelections.attendees?.length || 0
  const hasAttendees = attendeeCount > 0
  const programPrice = props.userSelections.userProgramDateTimeSelection?.price || 0
  const totalProgramPrice = programPrice * attendeeCount

  const totalAddOnPrice = (props.userSelections.userAddOnProgramSelections || []).reduce((sum, sel) => {
    const addonDef = selectedProgram?.addOnPrograms?.find(a =>
      sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
    )
    const pricePerAttendee = sel.disableDateTimeSelection
      ? (addonDef?.programPrice ?? 0)
      : (props.userSelections.userAddOnProgramDateTimeSelection?.price ?? addonDef?.programPrice ?? 0)
    return sum + pricePerAttendee
  }, 0) * attendeeCount

  const totalPrice = totalProgramPrice + totalAddOnPrice


  const currency = eventRegistrationRefreshModel.currency

  // Determine if we should show pricing based on context and country
  const showPricing = eventRegistrationRefreshModel.showPrice &&
    hasAttendees

  // Format currency
  const formatCurrency = (amount: number) => {
    if (!currency) return ''

    const formattedAmount = `$${amount.toLocaleString()}`
    return currency === 'CAD' ? `${formattedAmount} ${currency}` : formattedAmount
  }

  return (
    <div className="summary-card">
      {props.showValidationMessage && props.validationMessage && (
        <Alert icon="error">
          <p><strong>{props.validationMessage}</strong></p>
        </Alert>
      )}


      {
        props.showWarningMessage === true && props.warningMessage && (
          <Alert variant="info" icon="calendar"><p><strong>{props.warningMessage}</strong></p></Alert>
        )
      }

      {props.showMaxAttendeesMessage && props.maxAttendeesMessage && (
        <Alert icon="error" variant="info">
          <p><strong>{props.maxAttendeesMessage}</strong></p>
        </Alert>
      )}
      <div>
        <div className="summary-card-desktop">
          <h6 className="summary-card-title">{epiProps.programSummaryHeader}</h6>
          <ProgramSummarySelections
            userSelections={props.userSelections}
            eventRegistrationRefreshModel={eventRegistrationRefreshModel}
            showPricing={showPricing}
            formatCurrency={formatCurrency}
          />
        </div>
        <div className="summary-card-mobile">
          <div className="summary-card-mobile-header">

            <button
              className="summary-card-mobile-toggle"
              aria-expanded={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              {epiProps.programSummaryHeader}
              <svg className="svg-icon" aria-hidden="true" focusable="false">
                <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-chevron-down" />
              </svg>
            </button>
          </div>
          {isOpen && (
            <div className="summary-card-mobile-panel">
              <ProgramSummarySelections
                userSelections={props.userSelections}
                eventRegistrationRefreshModel={eventRegistrationRefreshModel}
                showPricing={showPricing}
                formatCurrency={formatCurrency}
              />
            </div>
          )}
        </div>
      </div>
      {
        (props.hideContinueButton === false || props.hideContinueButton === undefined) &&
        <button
          className="btn-secondary summary-card-cta"
          onClick={props.onContinue}>
          {currentStep == StepConstants.ReviewStep ? epiProps.addToCartCtaText : epiProps.programSummaryCTAText}
          {(currentStep == StepConstants.ReviewStep || currentStep == StepConstants.AttendeesStep) && props.isSubmitting == true && <LoadingSpinner />}
        </button>
      }

      {
        props.showShareSelectionsModalButton === true &&
        <button
          onClick={() => props.onShowShareSelectionsModal?.()}
          className="btn-tertiary summary-card-cta">{epiProps.shareSelectionsCtaTextProgramSummary} <svg className="svg-icon" aria-hidden="true" focusable="false">
            <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#share-icon" />
          </svg></button>
      }
    </div>
  )
}
